import { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, FullTextSearchInput } from 'components';
import { useLazyQuery } from '@apollo/client';
import * as O from 'fp-ts/Option';
import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/lib/function';

import {
  Maybe,
  Query,
  QueryGetAvailableModelVersionsArgs,
  QueryGetPricePredictionArgs,
  GetAvailableModelVersionsResponse,
  Vehicle,
} from 'generated/graphql';
import { prediction, modelVersions, comparison, country } from 'cache';
import { MODEL_VERSION } from 'apollo-client/queries/getAvailableModelVersion';
import { ModalContext } from 'providers/modal-provider';
import { EuiLoadingSpinner } from '@elastic/eui';
import { PRICE_PREDICTION } from 'apollo-client/queries/getPricePrediction';

import { PopupProps, STATUS } from 'components/Popup';

import * as S from './FiltersSidebar.styles';

export const CodesFilter = (): JSX.Element => {
  const { formatMessage } = useIntl();
  const { setModal } = useContext(ModalContext);
  const [currentCode, setCurrentCode] = useState<{ kind: string; value: string }>();

  const [onGetPricePrediction] = useLazyQuery<Query, QueryGetPricePredictionArgs & { withPriceTrend: boolean }>(
    PRICE_PREDICTION,
    {
      fetchPolicy: 'network-only',
      onError: () => {
        setModal<PopupProps>({
          type: 'info',
          props: {
            status: STATUS.ERROR,
            details:
              "Non è stato possibile calcolare il prezzo perché non ci sono annunci sufficienti nell'area geografica selezionata.",
            message: 'Cosa è successo?',
          },
        });
      },
      onCompleted: (res) => {
        if (!res.getPricePrediction.success) return console.log('ERROR');
        if (!res?.getPricePrediction?.vehicleRegistrationDateISO8601) return;
        prediction({
          lowerboundPredictedPrice: res?.getPricePrediction?.lowerboundPredictedPrice,
          upperboundPredictedPrice: res?.getPricePrediction?.upperboundPredictedPrice,
          predictedPrice: res?.getPricePrediction?.predictedPrice,
          vehicleKM: res?.getPricePrediction?.vehicleKM,
          vehicleRegistrationDateISO8601: res?.getPricePrediction?.vehicleRegistrationDateISO8601,
          averageListingDurationInDays: res?.getPricePrediction?.averageListingDurationInDays,
        });
      },
    }
  );

  const [onGetAvailableModelVersions, { loading, data }] = useLazyQuery<
    Query & GetAvailableModelVersionsResponse,
    QueryGetAvailableModelVersionsArgs
  >(MODEL_VERSION, {
    fetchPolicy: 'network-only',
    onError: () => {
      setModal<PopupProps>({
        type: 'info',
        props: {
          status: STATUS.ERROR,
          details: 'La targa selezionata non è presente nella banca dati motornet',
          message: 'Cosa è successo?',
        },
      });
    },
    onCompleted: (res) => {
      const identifiedVehicle = res?.getAvailableModelVersions?.identifiedVehicle;

      if (!res?.getAvailableModelVersions?.modelVersions) {
        setModal<PopupProps>({
          type: 'Popup',
          props: {
            status: STATUS.ERROR,
            message: formatMessage({ id: 'error' }),
            details: formatMessage({ id: 'code-not-found.error' }),
          },
        });
      }
      const availableModelVersions = res.getAvailableModelVersions?.modelVersions?.map(({ label, eurotaxCode }) => ({
        label,
        value: eurotaxCode,
      }));

      const setVersionAndCalculatePricePrediction = (
        version: Array<{ label: string; value: string }>,
        vehicle?: Maybe<Vehicle>
      ): void => {
        modelVersions(version);
        if (currentCode?.kind !== 'eurotax') {
          onGetPricePrediction({
            variables: {
              eurotaxCodes: [version[0]?.value],
              vehicleRegistrationDateISO8601: vehicle?.vehicleRegistrationDateISO8601,
              withPriceTrend: false,
              country: country(),
            },
          });
        }
        comparison({
          vehicleRegistrationPlate: currentCode?.kind === 'plate' ? currentCode?.value : undefined,
          eurotaxCode: version?.[0].value,
          brandName: vehicle?.brandName,
          modelName: vehicle?.modelName,
          modelVersion: vehicle?.modelName,
          vehicleRegistrationDateISO8601: vehicle?.vehicleRegistrationDateISO8601,
        });
      };

      pipe(
        O.fromNullable(availableModelVersions),
        O.map((versions) => (versions.length <= 1 ? E.left(versions) : E.right(versions))),
        O.map(
          E.fold(
            (version) => setVersionAndCalculatePricePrediction(version, identifiedVehicle),
            (versions) => {
              setModal({
                type: 'ModelVersionSelectModal',
                props: {
                  versions,
                  onSelectedVersion: (version: { label: string; value: string }[]) =>
                    setVersionAndCalculatePricePrediction(version, identifiedVehicle),
                },
              });
            }
          )
        )
      );

      setCurrentCode({ kind: '', value: '' });
    },
  });

  const vehicleIdentifierRemainingRequestsAmount =
    data?.getAvailableModelVersions?.vehicleIdentifierRemainingRequestsAmount;

  const handleSearch = (): void => {
    switch (currentCode?.kind as 'eurotax' | 'plate' | 'constructor_code') {
      case 'eurotax':
        onGetAvailableModelVersions({
          variables: {
            inputEurotaxCode: currentCode?.value,
          },
        });
        break;
      case 'plate':
        onGetAvailableModelVersions({
          variables: {
            inputPlateNumber: currentCode?.value,
          },
        });
        break;
      case 'constructor_code':
        onGetAvailableModelVersions({
          variables: {
            inputConstructorCode: currentCode?.value,
          },
        });
        break;
      default:
        break;
    }
  };

  return (
    <S.SidebarFilterWrapper>
      <FullTextSearchInput onChange={setCurrentCode} value={currentCode?.value as string} />
      <S.SidebarFilterCodeSearch>
        {vehicleIdentifierRemainingRequestsAmount && (
          <p>
            {formatMessage({ id: 'filters.availableRequests.label' })} {vehicleIdentifierRemainingRequestsAmount}
          </p>
        )}
        <Button disabled={!currentCode?.value} onClick={handleSearch} big>
          {loading ? <EuiLoadingSpinner size="m" /> : formatMessage({ id: 'filters.button.search' })}
        </Button>
      </S.SidebarFilterCodeSearch>
    </S.SidebarFilterWrapper>
  );
};
